import { Box } from '@mui/material';
import { useEffect, useRef, useState, type ReactNode } from 'react';
import { tokens } from '../theme';

const prefersReducedMotion = () =>
  typeof window !== 'undefined' &&
  window.matchMedia?.('(prefers-reduced-motion: reduce)').matches;

type RevealProps = {
  children: ReactNode;
  /** Stagger offset in ms. Keep cascades short — 40-80ms per item reads as one gesture. */
  delay?: number;
  /** Distance travelled, in px. Small is the point; this is a settle, not an entrance. */
  distance?: number;
  /** Softens the arrival. Reserved for the few moments that carry the page. */
  blur?: boolean;
};

/**
 * Reveals its children once, when they first scroll into view.
 *
 * The visible state is the default whenever motion is unwanted or unavailable
 * (reduced-motion, no IntersectionObserver), so content is never gated behind an
 * effect that might not run.
 */
const Reveal = ({ children, delay = 0, distance = 14, blur = false }: RevealProps) => {
  const ref = useRef<HTMLDivElement>(null);
  const [shown, setShown] = useState(
    () => prefersReducedMotion() || typeof IntersectionObserver === 'undefined'
  );

  useEffect(() => {
    if (shown) return;
    const node = ref.current;
    if (!node) return;

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setShown(true);
          observer.disconnect();
        }
      },
      // Fire a little before the element is fully on screen, so the motion has
      // finished by the time it reaches comfortable reading position.
      { threshold: 0.1, rootMargin: '0px 0px -8% 0px' }
    );

    observer.observe(node);
    return () => observer.disconnect();
  }, [shown]);

  return (
    <Box
      ref={ref}
      sx={{
        opacity: shown ? 1 : 0,
        transform: shown ? 'none' : `translateY(${distance}px)`,
        filter: blur && !shown ? 'blur(6px)' : 'blur(0px)',
        transition: [
          `opacity ${tokens.duration.reveal} ${tokens.ease.out} ${delay}ms`,
          `transform ${tokens.duration.reveal} ${tokens.ease.out} ${delay}ms`,
          `filter ${tokens.duration.reveal} ${tokens.ease.out} ${delay}ms`,
        ].join(', '),
        '@media (prefers-reduced-motion: reduce)': {
          opacity: 1,
          transform: 'none',
          filter: 'none',
        },
      }}
    >
      {children}
    </Box>
  );
};

export default Reveal;
